'use client';

import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { loadSubUniverseCatalog } from '@/lib/search/subUniverseCatalog';
import type { UniverseNode } from '@/lib/search/universeTree';
import { buildUniverseTree } from '@/lib/search/universeTree';

const EMPTY_UNIVERSE_TREE: UniverseNode[] = [];

const UniverseTreeContext =
  createContext<readonly UniverseNode[]>(EMPTY_UNIVERSE_TREE);

export interface UniverseTreeProviderProps {
  tree: readonly UniverseNode[];
  children: React.ReactNode;
}

/**
 * Arbre univers -> sous-univers chargé au SSR (SSRLabelsProvider). Contexte
 * distinct des libellés : un LabelsProvider imbriqué (modale d'alerte) ne le
 * masque pas.
 */
export function UniverseTreeProvider({
  tree,
  children,
}: UniverseTreeProviderProps): React.ReactElement {
  return (
    <UniverseTreeContext.Provider value={tree}>
      {children}
    </UniverseTreeContext.Provider>
  );
}

/**
 * Arbre des univers. Vide hors provider : on retombe alors sur l'affichage
 * sous-univers par sous-univers.
 */
export function useUniverseTree(): readonly UniverseNode[] {
  return useContext(UniverseTreeContext);
}

/**
 * L'arbre hérité (SSR) s'il existe, sinon celui du catalogue des sous-univers,
 * chargé côté navigateur — une seule requête par page, partagée avec le
 * filtre Univers. Le premier rendu reprend l'arbre hérité, côté serveur comme
 * côté client : pas d'écart d'hydratation.
 */
export function useUniverseTreeWithFallback(): readonly UniverseNode[] {
  const inherited = useUniverseTree();
  const hasInherited = inherited.length > 0;
  const [loaded, setLoaded] =
    useState<readonly UniverseNode[]>(EMPTY_UNIVERSE_TREE);

  useEffect(() => {
    if (hasInherited) return undefined;
    let active = true;
    loadSubUniverseCatalog().then((subUniverses) => {
      if (active) setLoaded(buildUniverseTree(subUniverses));
    });
    return () => {
      active = false;
    };
  }, [hasInherited]);

  return hasInherited ? inherited : loaded;
}

export interface UniverseTreeFallbackProviderProps {
  children: React.ReactNode;
}

/**
 * Garantit un arbre aux formulaires d'alerte, où qu'ils s'ouvrent : modale
 * montée par NiceModal à la racine, page d'un univers, compte, back-office.
 */
export function UniverseTreeFallbackProvider({
  children,
}: UniverseTreeFallbackProviderProps): React.ReactElement {
  return (
    <UniverseTreeContext.Provider value={useUniverseTreeWithFallback()}>
      {children}
    </UniverseTreeContext.Provider>
  );
}
